class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));

    const searchInput = this.querySelector('.facet-search__input');
    if (searchInput) {
      FacetFiltersForm.syncSearchCountFromProductCount();

      const urlParams = new URLSearchParams(window.location.search);
      const initialTerm = (urlParams.get('q') || '').trim().toLowerCase();
      const initialPage = parseInt(urlParams.get('page'), 10) || 1;
      if (initialTerm) {
        searchInput.value = initialTerm;
        FacetFiltersForm.applySearchFilter(initialTerm, initialPage);
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const facetDrawer = document.getElementById('FacetDrawer');
    const countContainer = document.getElementById('ProductCount');
    const countContainerMobile = document.getElementById('ProductCountMobile');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }   
    if (countContainerMobile){
      countContainerMobile.classList.add('loading');
    } 
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }
    if (facetDrawer){
      facetDrawer.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.applySearchFilter(FacetFiltersForm.getCurrentSearchTerm(), 1);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.applySearchFilter(FacetFiltersForm.getCurrentSearchTerm(), 1);
  }

  static getActiveSearchInput() {
    const inputs = Array.from(document.querySelectorAll('.facet-search__input'));
    return inputs.find((el) => el.offsetParent !== null) || inputs[0] || null;
  }

  static getCurrentSearchTerm() {
    const input = FacetFiltersForm.getActiveSearchInput();
    return input ? input.value.trim().toLowerCase() : '';
  }

  static getSearchBaseParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('q');
    params.delete('page');
    return params.toString();
  }

  static async fetchAllProducts(baseParams) {
    const sectionId = document.getElementById('product-grid').dataset.id;
    const buildUrl = (page) => {
      const params = new URLSearchParams(baseParams);
      params.set('section_id', sectionId);
      params.set('page', page);
      return `${window.location.pathname}?${params.toString()}`;
    };

    const parseGridItems = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const container = doc.getElementById('ProductGridContainer');
      const grid = doc.getElementById('product-grid');
      const items = [];
      if (grid) {
        grid.querySelectorAll(':scope > .grid__item').forEach((li) => {
          items.push({ title: (li.dataset.productTitle || '').toLowerCase(), html: li.outerHTML });
        });
      }
      const totalPages = container ? parseInt(container.dataset.totalPages, 10) || 1 : 1;
      return { items, totalPages };
    };

    const first = await fetch(buildUrl(1)).then((response) => response.text());
    const { items: firstItems, totalPages } = parseGridItems(first);

    const remainingPages = [];
    for (let page = 2; page <= totalPages; page++) {
      remainingPages.push(fetch(buildUrl(page)).then((response) => response.text()));
    }
    const remainingHtml = await Promise.all(remainingPages);

    let items = firstItems;
    remainingHtml.forEach((html) => {
      items = items.concat(parseGridItems(html).items);
    });

    return items;
  }

  static async ensureAllProductsFetched() {
    const key = FacetFiltersForm.getSearchBaseParams();
    if (!FacetFiltersForm.searchCache || FacetFiltersForm.searchCache.key !== key) {
      FacetFiltersForm.searchCache = {
        key,
        promise: FacetFiltersForm.fetchAllProducts(key),
      };
    }
    return FacetFiltersForm.searchCache.promise;
  }

  static setSearchLoading(isLoading) {
    const collection = document.querySelector('#ProductGridContainer .collection');
    if (collection) collection.classList.toggle('loading', isLoading);
  }

  static async applySearchFilter(term, page = 1) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    if (!term) {
      // No active search: the section fetch that triggered this already restored
      // Shopify's native grid + pagination markup, so leave it untouched here.
      FacetFiltersForm.renderSearchEmptyState(false, '');
      FacetFiltersForm.syncSearchCountFromProductCount();
      return;
    }

    FacetFiltersForm.setSearchLoading(true);
    let allItems;
    try {
      allItems = await FacetFiltersForm.ensureAllProductsFetched();
    } catch (e) {
      console.error(e);
      FacetFiltersForm.setSearchLoading(false);
      return;
    }
    FacetFiltersForm.setSearchLoading(false);

    if (FacetFiltersForm.getCurrentSearchTerm() !== term) return;

    const matches = allItems.filter((item) => item.title.includes(term));
    const gridContainer = document.getElementById('ProductGridContainer');
    const pageSize = parseInt(gridContainer && gridContainer.dataset.pageSize, 10) || 24;
    const totalPages = Math.max(1, Math.ceil(matches.length / pageSize));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * pageSize;
    const pageItems = matches.slice(start, start + pageSize);

    grid.innerHTML = pageItems.map((item) => item.html).join('');
    FacetFiltersForm.renderSearchEmptyState(matches.length === 0, term);
    FacetFiltersForm.renderSearchPagination({ term, currentPage, totalPages });
    FacetFiltersForm.updateSearchProductCount(matches.length);
  }

  static renderSearchEmptyState(isEmpty, term) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const container = grid.closest('.collection') || grid;
    let message = container.querySelector('.search-no-results');

    if (!isEmpty) {
      if (message) message.hidden = true;
      return;
    }

    if (!message) {
      message = document.createElement('p');
      message.className = 'search-no-results';
      container.insertBefore(message, grid.nextSibling);
    }

    const gridContainer = document.getElementById('ProductGridContainer');
    const template = (gridContainer && gridContainer.dataset.searchNoResultsTemplate) || 'No products found for "__TERM__"';
    message.textContent = template.replace('__TERM__', term);
    message.hidden = false;
  }

  static renderSearchPagination(state) {
    const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
    if (!wrapper) return;

    if (!state || state.totalPages <= 1) {
      wrapper.innerHTML = '';
      return;
    }

    const { currentPage, totalPages } = state;
    const gridContainer = document.getElementById('ProductGridContainer');
    const previousLabel = (gridContainer && gridContainer.dataset.paginationPreviousLabel) || 'Previous';
    const nextLabel = (gridContainer && gridContainer.dataset.paginationNextLabel) || 'Next';

    const numbers = [];
    for (let page = 1; page <= totalPages; page++) {
      numbers.push(
        `<li><button type="button" class="pagination__item${page === currentPage ? ' pagination__item--current' : ''}" data-search-page="${page}" ${page === currentPage ? 'aria-current="page"' : ''}>${page}</button></li>`
      );
    }

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    wrapper.innerHTML = `
      <nav class="pagination" role="navigation">
        <button type="button" class="pagination__nav pagination__nav--previous motion-reduce${isFirstPage ? ' pagination__nav--disabled' : ''}" data-search-page="${currentPage - 1}" ${isFirstPage ? 'disabled' : ''}>
          <span class="pagination__nav-label">${previousLabel}</span>
        </button>
        <ul class="pagination__numbers list-unstyled" role="list">${numbers.join('')}</ul>
        <button type="button" class="pagination__nav pagination__nav--next motion-reduce${isLastPage ? ' pagination__nav--disabled' : ''}" data-search-page="${currentPage + 1}" ${isLastPage ? 'disabled' : ''}>
          <span class="pagination__nav-label">${nextLabel}</span>
        </button>
      </nav>
    `;
  }

  static updateSearchProductCount(count) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const oneTemplate = gridContainer && gridContainer.dataset.countTemplateOne;
    const otherTemplate = gridContainer && gridContainer.dataset.countTemplateOther;
    if (!oneTemplate || !otherTemplate) return;

    const template = count === 1 ? oneTemplate : otherTemplate;
    const text = template.replace(/[\d,]+/, String(count));

    ['ProductCount', 'ProductCountMobile', 'ProductCountDesktop'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    });

    document.querySelectorAll('.facet-search__count').forEach((el) => {
      el.textContent = text;
      el.hidden = false;
    });
  }

  static syncSearchCountFromProductCount() {
    const source = document.getElementById('ProductCount');
    if (!source) return;

    document.querySelectorAll('.facet-search__count').forEach((el) => {
      el.textContent = source.textContent;
      el.hidden = false;
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    
    const layoutSwitcher = document.querySelector('#FacetSortFiltersForm layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.list-view__item--active'));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerMobile = document.getElementById('ProductCountMobile');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerMobile) {
      containerMobile.innerHTML = count;
      containerMobile.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    FacetFiltersForm.syncSearchCountFromProductCount();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetSortFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetQuickFiltersForm .js-filter');
    const matchesIndex = (element) => { 
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false; 
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

    const facetDrawer = document.getElementById('FacetDrawer');
    if (facetDrawer) {
      facetDrawer.classList.remove('loading');
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.facets__open', '.sorting', '.collection-filter-toggle'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('facet-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData);
  }

  mergeSearchParams(form, searchParams) {
    const params = this.createSearchParams(form);
    params.forEach((value, key) => {
      searchParams.append(key, value);
    });
    return searchParams;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (event.target && event.target.classList && event.target.classList.contains('facet-search__input')) {
      this.onSearchInput(event.target);
      return;
    }

    const currentForm = event.target.closest('form');
    let searchParams;

    if (currentForm.id === 'FacetFiltersFormMobile') {
      searchParams = this.createSearchParams(currentForm);
    }
    else {
      searchParams = new URLSearchParams();

      if (currentForm.id === 'FacetSortFiltersForm' && currentForm.dataset.filterType === 'drawer') {
        const mobileForm = document.getElementById('FacetFiltersFormMobile');
        searchParams = this.mergeSearchParams(mobileForm, searchParams);
        searchParams.delete('sort_by');
      }

      if (currentForm.id === 'FacetQuickFiltersForm') {
        // The quick filter bar duplicates a subset of the mobile drawer's controls (sort,
        // colors, size), so start from the drawer's full selection and let the quick bar's
        // own (fresher) values for those same fields win below.
        const mobileForm = document.getElementById('FacetFiltersFormMobile');
        if (mobileForm) searchParams = this.mergeSearchParams(mobileForm, searchParams);
        new FormData(currentForm).forEach((value, key) => searchParams.delete(key));
      }

      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetFiltersForm' || form.id === 'FacetSortFiltersForm' || form.id === 'FacetQuickFiltersForm') {
          searchParams = this.mergeSearchParams(form, searchParams);
        }
      });
    }

    // FIX: sort_by is a single-value field, but there are three independent sort_by
    // radio groups on the page (desktop FacetSortFiltersForm, mobile quick filter
    // bar FacetQuickFiltersForm, mobile drawer FacetFiltersFormMobile). The merge
    // loop above unconditionally pulls params from FacetSortFiltersForm and
    // FacetQuickFiltersForm every time (regardless of which one the user actually
    // touched), and mergeSearchParams() uses append() rather than set(). That
    // leaves two (or more) sort_by entries in the same URLSearchParams object —
    // one fresh (from whichever control the user just changed) and one stale
    // (from whichever sort control the user did NOT touch, still holding its
    // last-rendered value). Which one wins server-side then depends on param
    // order, not on user intent.
    //
    // Fix: whichever form the user's change happened in is authoritative for
    // sort_by. IMPORTANT: for radio inputs, `querySelector('[name="sort_by"]')`
    // (no :checked) returns the FIRST radio in the group, not necessarily the
    // selected one — it must be qualified with :checked, otherwise this silently
    // grabs the wrong value.
    const currentFormSortInput = currentForm.querySelector('[name="sort_by"]:checked');
    if (currentFormSortInput) {
      searchParams.delete('sort_by');
      searchParams.set('sort_by', currentFormSortInput.value);
    } else if (searchParams.getAll('sort_by').length > 1) {
      // The changed field wasn't sort_by itself, but duplicates still crept in
      // from the merge loop — collapse to the last one rather than leaving both.
      const values = searchParams.getAll('sort_by');
      searchParams.delete('sort_by');
      searchParams.set('sort_by', values[values.length - 1]);
    }

    // The search box can live in a different form (or outside any filter form
    // entirely) than whichever one triggered this change, so its value is never
    // trusted from FormData here — always re-applied from the live input instead.
    searchParams.delete('q');
    const currentSearchTerm = FacetFiltersForm.getCurrentSearchTerm();
    if (currentSearchTerm) searchParams.set('q', currentSearchTerm);

    this.onSubmitForm(searchParams.toString(), event);
    /*
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
    */
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.renderPage(new URL(event.currentTarget.href).searchParams.toString());
  }

  onSearchInput(input) {
    const term = input.value.trim().toLowerCase();

    document.querySelectorAll('.facet-search__input').forEach((element) => {
      if (element !== input) element.value = input.value;
    });

    const params = new URLSearchParams(window.location.search);
    params.delete('page');

    if (!term) {
      params.delete('q');
      FacetFiltersForm.renderPage(params.toString());
      return;
    }

    params.set('q', term);
    FacetFiltersForm.updateURLHash(params.toString());
    FacetFiltersForm.applySearchFilter(term, 1);
  }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-filter-toggle]');
  if (!toggle) return;

  const layout = toggle.closest('.facets-vertical--toggleable');
  if (!layout) return;

  const isCollapsed = layout.classList.toggle('facets-vertical--collapsed');
  toggle.setAttribute('aria-expanded', (!isCollapsed).toString());
});

document.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
  const layout = toggle.closest('.facets-vertical--toggleable');
  toggle.setAttribute('aria-expanded', Boolean(layout && !layout.classList.contains('facets-vertical--collapsed')).toString());
});

document.addEventListener('click', (event) => {
  const clearButton = event.target.closest('.facet-search__clear');
  if (!clearButton) return;

  const input = clearButton.closest('.facet-search').querySelector('.facet-search__input');
  if (!input || !input.value) return;

  input.value = '';
  const form = input.closest('facet-filters-form');
  if (form && typeof form.onSearchInput === 'function') {
    form.onSearchInput(input);
  } else {
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
  input.focus();
});

document.addEventListener('click', (event) => {
  const pageButton = event.target.closest('[data-search-page]');
  if (!pageButton || pageButton.disabled) return;

  const term = FacetFiltersForm.getCurrentSearchTerm();
  if (!term) return;

  const page = parseInt(pageButton.dataset.searchPage, 10);
  if (!page || page < 1) return;

  const params = new URLSearchParams(window.location.search);
  params.set('q', term);
  params.set('page', page);
  FacetFiltersForm.updateURLHash(params.toString());
  FacetFiltersForm.applySearchFilter(term, page);

  const grid = document.getElementById('product-grid');
  if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// Dim the product grid behind the mobile quick-filter dropdowns while one is open.
// The 'toggle' event doesn't bubble in every browser, so this listens on the
// capturing phase instead of delegating from a single ancestor.
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.closest('.mobile-quick-filters')) return;

  const gridContainer = document.querySelector('footer.custom-footer');
  if (!gridContainer) return;

  const anyOpen = Boolean(document.querySelector('.mobile-quick-filters details[open]'));
  gridContainer.classList.toggle('quick-filters-overlay', anyOpen);
}, true);

document.addEventListener('click', (event) => {
  const gridContainer = document.querySelector('footer.custom-footer');
  if (!gridContainer || !gridContainer.classList.contains('quick-filters-overlay')) return;
  if (event.target.closest('.mobile-quick-filters')) return;

  document.querySelectorAll('.mobile-quick-filters details[open]').forEach((details) => details.removeAttribute('open'));
  gridContainer.classList.remove('quick-filters-overlay');
});

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}
customElements.define('facet-remove', FacetRemove);

class PriceRange extends HTMLElement {
	constructor() {
    super();

    this.min = Number(this.dataset.min);
		this.max = Number(this.dataset.max);
    this.track = this.querySelector(".price-range__track");
		this.handles = [...this.querySelectorAll(".price-range__thumbs")];
		this.startPos = 0;
		this.activeHandle;
		
		this.handles.forEach(handle => {
			handle.addEventListener("mousedown", this.startMove.bind(this));
		})
		
		window.addEventListener("mouseup", this.stopMove.bind(this));

    this.querySelectorAll('input').forEach(
      element => element.addEventListener('change', this.onRangeChange.bind(this))
    );
	}

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }
	
	startMove(e) {
		this.startPos = e.offsetX;
		this.activeHandle = e.target;
		this.moveListener = this.move.bind(this);
		window.addEventListener("mousemove", this.moveListener);
	}
	
	move(e) {
		const isLower = this.activeHandle.classList.contains("is-lower");
		const property = isLower ? "--progress-lower" : "--progress-upper";
		const parentRect = this.track.getBoundingClientRect();
		const handleRect = this.activeHandle.getBoundingClientRect();
		let newX = e.clientX - parentRect.x - this.startPos;
		
    if (isLower) {
			const otherX = parseInt(this.style.getPropertyValue("--progress-upper"));
      const percentageX = otherX * parentRect.width / 100;
			newX = Math.min(newX, percentageX - handleRect.width);
			newX = Math.max(newX, 0 - handleRect.width/2);
		}
    else {
			const otherX = parseInt(this.style.getPropertyValue("--progress-lower"));
      const percentageX = otherX * parentRect.width / 100;
			newX = Math.max(newX, percentageX);
			newX = Math.min(newX, parentRect.width - handleRect.width/2);
		}

    const percentage = (newX + handleRect.width/2) / parentRect.width;
    const valuenow = this.calcHandleValue(percentage);
    this.style.setProperty(property, percentage * 100 + "%");
		this.activeHandle.ariaValueNow = valuenow;

    const output = this.activeHandle.nextElementSibling;
    const text = output.querySelector('.price-range__output-text');
    text.innerHTML = valuenow;

    const inputs = this.querySelectorAll('input');
    const input = isLower ? inputs[0] : inputs[1];
    input.value = valuenow;
    
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
	}
	
	calcHandleValue(percentage) {
		return Math.round(percentage * (this.max - this.min) + this.min);
	}
	
	stopMove() {
		window.removeEventListener("mousemove", this.moveListener);
    const form = this.closest('form');
    
    if (this.activeHandle && form) form.dispatchEvent(new Event('input'));
	}

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class LayoutSwitcher extends HTMLElement {
	constructor() {
    super();

    this.cookieName = 'beyours:collection-layout';

    this.initLayoutMode();
    this.querySelectorAll('.list-view__item').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
	}
	
  onButtonClick(event) {
    // A real click event's target can be an inner icon/label instead of the
    // button itself (more likely on touch, where the whole small button is
    // basically just the icon) — currentTarget is always the button the
    // listener is bound to. When called directly with a plain element
    // (see renderProductGridContainer / initLayoutMode), neither property
    // exists on it, so it falls through to using the element itself.
    const target = event.currentTarget || event.target || event;
    this.changeLayoutMode(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (isStorageSupported('local')) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`.list-view__item[data-layout-mode="${layoutMode}"]`);

        if (target) {
          target.click();
          return;
        }
      }
    }

    // No saved preference yet: default responsively — 2 columns on mobile,
    // 4 on desktop — instead of relying on the merchant's single
    // products_per_row setting for every viewport. Triggering a real click
    // (rather than calling changeLayoutMode directly) runs it through the
    // exact same path a manual click does.
    const isMobile = !window.matchMedia('(min-width: 750px)').matches;
    const defaultTarget = isMobile
      ? this.querySelector('.list-view__item')
      : this.querySelector('.list-view__item[data-layout-mode="grid-4"]');

    if (defaultTarget) defaultTarget.click();
  }

  changeLayoutMode(target, layoutMode) {
    const productGrid = document.getElementById('product-grid');

    if (productGrid.classList.contains('collection--empty')) {
      return;
    }

    const removedClass = ['list', 'grid', 'grid--1-col', 'grid--2-col', 'grid--3-col-tablet', 'grid--2-col-desktop', 'grid--3-col-desktop', 'grid--4-col-desktop'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = [];
    switch (layoutMode) {
      case 'list':
        addedClass = ['grid', 'grid--1-col', 'grid--2-col-desktop', 'list'];
        break;

      case 'grid-2':
        addedClass = ['grid', 'grid--2-col'];
        break;

      case 'grid-3':
        addedClass = ['grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--3-col-desktop'];
        break;

      case 'grid-4':
        addedClass = ['grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--4-col-desktop'];
        break;
    }
    addedClass.forEach((added) => {
      productGrid.classList.add(added);
    });

    // Query document-wide (not just `this`) so the mobile and desktop layout
    // switchers stay mirrored even though only one is visible at a time.
    document.querySelectorAll('layout-switcher .list-view__item').forEach(
      (button) => button.classList.remove('list-view__item--active')
    );

    document.querySelectorAll(`layout-switcher .list-view__item[data-layout-mode="${layoutMode}"]`).forEach(
      (button) => button.classList.add('list-view__item--active')
    );

    if (isStorageSupported('local')) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);

class StickyFacetFilters extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.parentNode.offsetTop) {
      window.requestAnimationFrame(this.reveal.bind(this));
    } else {
      window.requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.classList.add('shopify-section-filters-sticky');
  }

  reset() {
    this.classList.remove('shopify-section-filters-sticky');
  }
}
customElements.define('sticky-facet-filters', StickyFacetFilters);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();

    const attributes = {
      expanded: 'aria-expanded'
    };

    const button = this.querySelector('.button-show-more');
    button.addEventListener('click', () => {
      const filter = this.closest('.js-filter');
      filter.setAttribute(
        attributes.expanded,
        (filter.getAttribute(attributes.expanded) === 'false').toString()
      );

      this.querySelectorAll('.label-show-more').forEach(element => element.classList.toggle('hidden'));
    });
  }
}
customElements.define('show-more-button', ShowMoreButton);

document.addEventListener('DOMContentLoaded', function () {
  if (window.innerWidth < 768) {
    var firstItem = document.querySelector('.mobile-facets__view-switcher .list-view__item');
    if (firstItem) {
      firstItem.click();
    }
  }
});